import { defineStore } from 'pinia'
import { ref } from 'vue'
import { BaseStore } from './baseStore'
import { where, type QueryConstraint } from 'firebase/firestore'

export interface CostType {
  id?: string
  costCenterId: string
  organizationId: string
  code: string
  name: string
  description?: string
  active?: boolean
  createdAt?: string
  updatedAt?: string
}

class CostTypeStore extends BaseStore {
  constructor() {
    super('cost-types')
  }

  async fetchCostTypes(organizationId: string) {
    if (!organizationId) {
      throw new Error('organizationId is required')
    }
    const filters: QueryConstraint[] = [
      where('organizationId', '==', organizationId)
    ]
    return await this.fetchCollection<CostType>(filters)
  }

  async fetchCostTypesForCenter(organizationId: string, costCenterId: string) {
    if (!organizationId || !costCenterId) {
      throw new Error('organizationId and costCenterId are required')
    }
    const filters: QueryConstraint[] = [
      where('organizationId', '==', organizationId),
      where('costCenterId', '==', costCenterId)
    ]
    return await this.fetchCollection<CostType>(filters)
  }

  async addCostType(costType: CostType) {
    if (!costType.organizationId || !costType.costCenterId) {
      throw new Error('organizationId and costCenterId are required')
    }
    return await this.addDocument<CostType>({
      ...costType,
      active: true
    })
  }

  async updateCostType(id: string, costType: Partial<CostType>) {
    if (!id) {
      throw new Error('id is required')
    }
    return await this.updateDocument<CostType>(id, costType)
  }

  async deleteCostType(id: string) {
    if (!id) {
      throw new Error('id is required')
    }
    return await this.deleteDocument(id)
  }
}

export const useCostTypeStore = defineStore('costType', () => {
  const costTypeStore = new CostTypeStore()
  const costTypes = ref<CostType[]>([])
  const loading = ref(false)

  const fetchCostTypes = async (organizationId: string) => {
    if (!organizationId) {
      console.error('organizationId is required')
      return []
    }
    loading.value = true
    try {
      console.log('Fetching cost types for org:', organizationId)
      const types = await costTypeStore.fetchCostTypes(organizationId)
      console.log('Fetched cost types:', types)
      
      if (!types) {
        console.error('No types returned from fetchCostTypes')
        return []
      }
      
      costTypes.value = types
      return types
    } catch (error) {
      console.error('Erro ao buscar tipos de custo:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const fetchCostTypesForCenter = async (organizationId: string, costCenterId: string) => {
    if (!organizationId || !costCenterId) {
      console.error('organizationId and costCenterId are required')
      return []
    }
    loading.value = true
    try {
      console.log('Fetching cost types for org:', organizationId, 'and center:', costCenterId)
      const types = await costTypeStore.fetchCostTypesForCenter(organizationId, costCenterId)
      console.log('Fetched cost types for center:', types)
      
      if (!types) {
        console.error('No types returned from fetchCostTypesForCenter')
        return []
      }
      
      costTypes.value = types
      return types
    } catch (error) {
      console.error('Erro ao buscar tipos de custo do centro:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const addCostType = async (costType: Omit<CostType, 'id' | 'active' | 'createdAt' | 'updatedAt'>) => {
    if (!costType.organizationId || !costType.costCenterId) {
      throw new Error('organizationId and costCenterId are required')
    }
    try {
      const newCostType = await costTypeStore.addCostType({
        ...costType,
        active: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      costTypes.value.push(newCostType)
      return newCostType
    } catch (error) {
      console.error('Erro ao adicionar tipo de custo:', error)
      throw error
    }
  }

  const updateCostType = async (id: string, costType: Partial<CostType>) => {
    if (!id) {
      throw new Error('id is required')
    }
    try {
      await costTypeStore.updateCostType(id, {
        ...costType,
        updatedAt: new Date().toISOString()
      })
      const index = costTypes.value.findIndex(ct => ct.id === id)
      if (index !== -1) {
        costTypes.value[index] = { ...costTypes.value[index], ...costType }
      }
    } catch (error) {
      console.error('Erro ao atualizar tipo de custo:', error)
      throw error
    }
  }

  const deleteCostType = async (id: string) => {
    if (!id) {
      throw new Error('id is required')
    }
    try {
      await costTypeStore.deleteCostType(id)
      costTypes.value = costTypes.value.filter(ct => ct.id !== id)
    } catch (error) {
      console.error('Erro ao excluir tipo de custo:', error)
      throw error
    }
  }

  return {
    costTypes,
    loading,
    fetchCostTypes,
    fetchCostTypesForCenter,
    addCostType,
    updateCostType,
    deleteCostType
  }
})
